import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ChatCircleText, Heart, PaperPlaneTilt, Star } from "@phosphor-icons/react";

export default function FeedbackLounge() {
    const [posts, setPosts] = useState([]);
    const [content, setContent] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = () => api.get("/feedback").then((r) => setPosts(r.data)).catch(() => {});

    useEffect(() => {
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Intentionally run only on mount - load is stable

    const submit = async (e) => {
        e.preventDefault();
        if (!content.trim()) return;
        setSubmitting(true);
        try {
            await api.post("/feedback", { content });
            setContent("");
            await load();
            toast.success("BROADCAST SENT // +10 PTS");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setSubmitting(false);
        }
    };

    const like = async (id) => {
        try {
            await api.post(`/feedback/${id}/like`);
            await load();
        } catch (err) {
            console.error("Failed to like feedback:", err);
        }
    };

    return (
        <section data-testid="feedback-lounge-section" className="bg-[#0A0A0A] border border-white/10 relative overflow-hidden h-full flex flex-col">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <ChatCircleText size={20} className="text-electric-blue" weight="duotone" />
                    <div>
                        <p className="label-mono text-electric-blue">FEEDBACK LOUNGE</p>
                        <h2 className="font-heading font-bold text-lg">Operator Broadcast</h2>
                    </div>
                </div>
            </div>

            <form onSubmit={submit} className="p-5 border-b border-white/10 flex gap-2">
                <input
                    data-testid="feedback-input"
                    type="text"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Share an upload, ask for feedback, drop a win..."
                    className="flex-1 bg-[#060808] border border-white/10 px-3 py-2 text-sm outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue transition-all"
                />
                <button
                    type="submit"
                    disabled={submitting || !content.trim()}
                    data-testid="feedback-submit-btn"
                    className="bg-electric-blue hover:bg-electric-blue-dark disabled:opacity-50 text-white px-4 py-2 transition-all hover:shadow-[0_0_18px_rgba(0,123,255,0.5)] flex items-center gap-2"
                >
                    <PaperPlaneTilt size={14} weight="bold" />
                </button>
            </form>

            <div className="flex-1 overflow-y-auto scrollbar-thin divide-y divide-white/5 max-h-[420px]">
                {posts.length === 0 && (
                    <div className="p-8 text-center label-mono text-muted-foreground">
                        BE THE FIRST TO BROADCAST
                    </div>
                )}
                {posts.map((p, i) => (
                    <div key={p.id} data-testid={`feedback-post-${i}`} className={`px-5 py-4 hover:bg-electric-blue/5 transition-colors ${p.lifetime_member ? "border-l-2 border-yellow-400/40" : ""}`}>
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex items-center gap-2">
                                <div className={`w-7 h-7 flex items-center justify-center font-mono text-xs ${p.lifetime_member ? "bg-yellow-400/15 border border-yellow-400/40 text-yellow-300" : "bg-electric-blue/15 border border-electric-blue/30 text-electric-blue"}`}>
                                    {p.display_name?.[0]?.toUpperCase() || "?"}
                                </div>
                                <span className={`font-semibold text-sm ${p.lifetime_member ? "text-yellow-200" : ""}`}>{p.display_name}</span>
                                {p.lifetime_member && (
                                    <span data-testid={`feedback-lifetime-badge-${i}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-yellow-400/10 border border-yellow-400/40 text-yellow-400">
                                        <Star size={9} weight="fill" />
                                        <span className="label-mono text-[8px]">LIFETIME</span>
                                    </span>
                                )}
                            </div>
                            <span className="label-mono text-[9px] text-muted-foreground">
                                {new Date(p.created_at).toLocaleDateString()}
                            </span>
                        </div>
                        <p className="text-sm text-foreground/90 mb-2 leading-relaxed">{p.content}</p>
                        <button
                            onClick={() => like(p.id)}
                            data-testid={`feedback-like-${i}`}
                            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-pink-400 transition-colors"
                        >
                            <Heart size={12} weight={p.likes > 0 ? "fill" : "regular"} />
                            <span>{p.likes}</span>
                        </button>
                    </div>
                ))}
            </div>
        </section>
    );
}
